import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { accountUsersService } from '@/services';
import { invalidateQueries, queryClient } from '@/lib/query-client';

const accountUsersQueryAtom = atomWithQuery(() => ({
  queryKey: ['account-users'],
  queryFn: accountUsersService.getAccountUsers,
  staleTime: 5 * 60 * 1000
}));

const addAccountUserMutationAtom = atomWithMutation(() => ({
  mutationKey: ['account-users', 'add'],
  mutationFn: ({ name, email, role }: { name: string; email: string; role: number }) =>
    accountUsersService.addAccountUser(name, email, role),
  onSuccess: () => {
    invalidateQueries(['account-users']);
  }
}));

const addAliasMutationAtom = atomWithMutation(() => ({
  mutationKey: ['account-users', 'alias', 'add'],
  mutationFn: ({ accountUserId, email }: { accountUserId: number; email: string }) =>
    accountUsersService.addAlias(accountUserId, email),
  onSuccess: (_data, { accountUserId }) => {
    queryClient.invalidateQueries({ queryKey: ['account-users', 'aliases', accountUserId] });
  }
}));

const removeAliasMutationAtom = atomWithMutation(() => ({
  mutationKey: ['account-users', 'alias', 'remove'],
  mutationFn: ({ accountUserId, aliasId }: { accountUserId: number; aliasId: number }) =>
    accountUsersService.removeAlias(accountUserId, aliasId),
  onSuccess: (_data, { accountUserId }) => {
    queryClient.invalidateQueries({ queryKey: ['account-users', 'aliases', accountUserId] });
  }
}));

export function useAccountUsers() {
  const [query] = useAtom(accountUsersQueryAtom);
  return query;
}

export function useAddAccountUser() {
  const [mutation] = useAtom(addAccountUserMutationAtom);
  return mutation;
}

export function useAccountUserAliases(accountUserId: number | undefined) {
  const aliasesQueryAtom = atomWithQuery(() => ({
    queryKey: ['account-users', 'aliases', accountUserId],
    queryFn: () => accountUsersService.listAliases(accountUserId as number),
    enabled: typeof accountUserId === 'number',
    staleTime: 60 * 1000
  }));
  const [query] = useAtom(aliasesQueryAtom);
  return query;
}

export function useAddAccountUserAlias() {
  const [mutation] = useAtom(addAliasMutationAtom);
  return mutation;
}

export function useRemoveAccountUserAlias() {
  const [mutation] = useAtom(removeAliasMutationAtom);
  return mutation;
}
